import { useEffect, useState } from 'react'
import { useGenericRequestHandler } from '../../handlers'
import type {
  ProductCurrencies,
  ProductCurrencyMap,
  ProductsDTO,
  ProductsWithSymbols
} from '../../models'

const PRODUCT_UNLIMITED_VOLUME = 'unlimited_volume'
const PRODUCT_ESSENTIAL_VOLUME = 'essential_volume'

function decorateWithCurrencySymbols(products: ProductsDTO) {
  const symbolMap: Partial<ProductCurrencyMap> = {}

  for (const row of products.currencies) {
    symbolMap[row.currencyCode as ProductCurrencies] = row.symbol
  }

  products.currencySymbols = symbolMap as ProductCurrencyMap

  return products as ProductsWithSymbols
}

function schoolProductsAreValid(schoolProducts: ProductsWithSymbols): boolean {
  return (
    'products' in schoolProducts &&
    PRODUCT_ESSENTIAL_VOLUME in schoolProducts.products &&
    PRODUCT_UNLIMITED_VOLUME in schoolProducts.products
  )
}

export interface UseProductsQueryProps {
  queryParams: {
    provider: string
  }
  forSchools?: boolean
  enabled?: boolean
}

export const useProductsQuery = (
  options: UseProductsQueryProps = {
    queryParams: { provider: 'chargebee' },
    forSchools: false,
    enabled: true
  }
) => {
  const { data, error, loading, ...rest } = useGenericRequestHandler({
    route: `c/products?${new URLSearchParams(options.queryParams).toString()}`,
    method: 'GET',
    enabled: options.enabled
  })
  const [newData, setNewData] = useState<ProductsWithSymbols | null>(null)

  useEffect(() => {
    if (!options.enabled || loading || error || newData || !data) return

    const products = decorateWithCurrencySymbols(data)

    if (options.forSchools) {
      if (!schoolProductsAreValid(data)) {
        throw new Error('Invalid school products')
      }
    }

    setNewData(products)
  }, [loading, error, data, newData])

  return { data: newData, loading, error, ...rest }
}
